/**
 * Every dimension and colour the chart uses, as a custom property. Defaults reproduce the
 * current implementation exactly, at the 62.5% rem base (1rem = 10px).
 *
 * `--gantt-px-per-day` is a FALLBACK only: `gantt-chart.tsx` writes it from the time scale, so
 * the scale stays the single source of truth for geometry.
 */
.root {
  --gantt-px-per-day: 48px;
  /* The virtualizer's fixed row-to-row offset, and the ONE row dimension JS owns:
     `gantt-chart.tsx` writes it from `row-list.tsx`'s `ROW_HEIGHT_PX`, so nothing can offset a
     row by one distance and paint it at another. This value is the documented FALLBACK, same
     precedent as `--gantt-px-per-day` above. */
  --gantt-row-stride: 4.4rem;
  /* Yours to change: nothing writes it inline, so any rule matching the root element or a box
     below it reaches `.row`, which spends it as bottom padding. Note the declaration on the next
     line is ON `.root`, and a declared value beats an inherited one — so setting this token on an
     ancestor ABOVE `.root` does not arrive, `!important` included. Override it with a rule that
     matches the root element itself (e.g. `.wrapper > div { --gantt-row-gap: … }`).
     Changing it moves the boundary between painted row and empty gap WITHIN the stride, so it can
     never desynchronise the virtualizer — which is exactly why the height below is derived. */
  --gantt-row-gap: 0.4rem;
  --gantt-row-height: calc(var(--gantt-row-stride) - var(--gantt-row-gap));
  --gantt-sidebar-width: 24rem;
  --gantt-bar-height: 3.2rem;
  --gantt-bar-radius: var(--border-radius-small);
  /* The OPAQUE header height — month band (2.8rem) + day cells (3.6rem).
     The old module's header box was 9.4rem, but its gradient went transparent at 6.4rem and the
     body was pulled back up under that band by `margin-top: -3.2rem`
     (`gantt-chart/chart-wrapper.tsx:124-167`), so only 6.4rem was ever visible. Carrying 9.4rem
     over as solid layout space — with no pull-up — added ~3rem of empty chrome above row 1.
     That lower band was an OVERLAY, not header height: it exists so the date-tracker pill can
     hang over the chart. Phase 2 restores it that way (an element overlapping the body), NOT by
     making this token taller again. */
  --gantt-header-height: 6.4rem;

  --gantt-weekday-bg: var(--color-surfaces-bg-elevation-2);
  --gantt-weekend-bg: var(--color-surfaces-bg-elevation-1);
  --gantt-grid-line: var(--color-surfaces-bg-elevation-4);
  /* Shows only through the one-pixel column edges `gridBackground` leaves open, where it reads
     as the dashed half of the day separator. Same colour the old chart used for it. */
  --gantt-grid-dash: var(--color-surfaces-bg-elevation-2);
  --gantt-today-line: var(--color-shades-dark-raspberry);
  --gantt-sidebar-bg: var(--color-surfaces-bg-elevation-1);
  --gantt-accent-1: var(--color-actions-main-action);
  --gantt-accent-2: var(--color-actions-main-action);

  display: flex;
  flex-direction: column;
  flex: 1;
  /* HEIGHT-BOUNDED, so `.scroller` below is the box that scrolls. A flex item's automatic minimum
     size is its CONTENT's — so in a column parent (`multi-projects-calendar.module.scss`'s
     `.container`) `flex: 1` alone does not stop this box growing to the full height of every row:
     `.scroller` then has nothing left to scroll, the parent scrolls instead, the sticky header
     scrolls off the top with it, and the horizontal scrollbar — `.scroller`'s bottom edge — ends
     up parked below the last row instead of at the bottom of the screen. Measured in Chromium:
     without this line the scrollport is 2064px tall inside a 400px viewport; with it, 344px.
     Only the automatic minimum needs overriding; `flex: 1` already asks for the right size.
     A parent that is not a flex container has to bound this box itself — see
     `project-calendar.module.scss`'s `.nextChartWrapper`. */
  min-height: 0;
  min-width: 100%;
  max-width: 100%;
  background-color: var(--gantt-weekday-bg);
}

/* The single scroll container. Nothing between it and a sticky descendant may set overflow on
   the sticky axis, or sticky silently stops working. */
.scroller {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

/* Sized to the whole scrollable content, NOT to the scrollport: `.rows`, `.row` and the header
   all take their width from this box, and a `position: sticky` child is clamped to its
   containing block. Were this box the width of the viewport, the sticky row headers and the
   sticky header corner would scroll out of sight the moment the user passed one viewport width.
   `--gantt-range-width` is written by `gantt-chart.tsx` and defined nowhere else. */
.canvas {
  position: relative;
  width: calc(var(--gantt-sidebar-width) + var(--gantt-range-width));
  min-width: 100%;
  /* The day columns are painted as a background on this element, so it has to fill the scrollport
     or the grid stops at the last row and the space below it is flat. The old chart carried
     `height: 100%; min-height: 100%` for the same reason (`gantt-chart/components/chart.tsx`).
     `.scroller` takes a definite height from `flex: 1`, so the percentage resolves. */
  min-height: 100%;
  /* An explicit stacking context, so `.todayLine` can sit at a negative z-index and land BETWEEN
     this element's day-grid background and its content. Without it the line escapes to whatever
     ancestor context exists and paints under the grid, i.e. invisibly. */
  z-index: 0;
}

/* The timeline header's row group, and the element that actually sticks.
   `position: sticky` MUST live here rather than on `.header` inside it. A sticky box is clamped
   to its containing block, and this wrapper's containing block is `.canvas` — the whole
   scrollable content, so the permitted offset range is the full scroll height. Put sticky on
   `.header` instead and its containing block is this wrapper, a block box exactly one header
   tall: the permitted range collapses to [0, 0] and the header never travels at all.

   `background: inherit` reaches `.canvas`, whose `background-image` / `-size` / `-position` are
   written by `gantt-chart.tsx`. This element is `.canvas`'s direct child and shares its origin,
   so the header's day columns line up with the body's without being computed twice. Inheriting
   through a second wrapper would resolve to `none`, which is exactly the defect this replaced.

   `inherit` takes `.canvas`'s background-COLOUR too, and that is `transparent` — colour is not
   inherited from `.root`. Without the second declaration the header is opaque only where the
   gradient is, and rows scroll through as dashes at exactly the one-pixel column edges
   `gridBackground` leaves open. Colour paints under every image layer, so alignment is
   untouched. */
.headerGroup {
  position: sticky;
  top: 0;
  z-index: 11;
  background: inherit;
  background-color: var(--gantt-weekday-bg);
}

/* See `gantt-chart.tsx` for why this is an absolute layer wrapping a sticky child rather than one
   box doing both. `height: 100%` resolves against the layer, which `top`/`bottom` have already
   stretched to the full canvas — the scrollport's height at minimum. */
.sidebarFillLayer {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
  z-index: -1;
}

.sidebarFill {
  position: sticky;
  left: 0;
  width: var(--gantt-sidebar-width);
  height: 100%;
  background-color: var(--gantt-sidebar-bg);
}

.todayLine {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--today-x);
  width: 0.2rem;
  transform: translateX(-50%);
  background-color: var(--gantt-today-line);
  pointer-events: none;
  /* Above `.canvas`'s day-grid background, below every row — so it runs behind the sticky sidebar
     and behind the bars, matching the old guide (z-index 1 under a strip layer at 3 and a sidebar
     at 10). A positive z-index cannot express that here: `.row` sets a `transform`, which makes it
     a stacking context, so the sidebar cell's own z-index is sealed inside its row and can never
     out-rank a line sitting in the ancestor context. Negative is the only side of the row that is
     reachable from out here. */
  z-index: -1;
}
